import { useState } from "react";
import { Trash2 } from "lucide-react";
import { fmt } from "@/lib/load/products";
import { TareHelper } from "@/components/load/TareHelper";
import { METRIC, toShown, fromShown } from "@/lib/units";

// Weights are stored in lb; metric drivers type kg.
const shownLb = (lb) => String(Math.round(toShown(lb, "lb")));
const toLb = (v) => Math.round(fromShown(Number(v), "lb"));
// Unchanged fields keep their exact stored value, so metric round-trips never drift.
const keep = (shown, orig, conv) => (orig != null && shown === String(Math.round(toShown(orig, conv === toLb ? "lb" : "gal"))) ? orig : conv(shown));
const shownGal = (g) => String(Math.round(toShown(g, "gal")));
const toGal = (v) => Math.round(fromShown(Number(v), "gal"));

const blankRow = (n) => ({ name: `C${n}`, capacity: shownGal(1000), locked_dry: false, double_bulkhead: false });
const digits = (v) => v.replace(/\D/g, "");

export const TrailerModal = ({ initial, isNew, canDelete, onSave, onDelete, onClose, t }) => {
  const [name, setName] = useState(initial.name);
  const [tare, setTare] = useState(shownLb(initial.tare_weight ?? 28000));
  const [maxGross, setMaxGross] = useState(shownLb(initial.max_gross ?? 80000));
  const [dieselDouble, setDieselDouble] = useState(!!initial.diesel_in_double);
  const [rows, setRows] = useState(initial.compartments.map((c) => ({ ...c, capacity: shownGal(c.capacity) })));
  const [error, setError] = useState("");
  const setRow = (i, k, v) => { setError(""); setRows(rows.map((r, j) => (j === i ? { ...r, [k]: v } : r))); };
  const addRow = () => { let n = rows.length + 1; while (rows.some((r) => r.name === `C${n}`)) n++; setRows([...rows, blankRow(n)]); };
  const total = rows.reduce((a, r) => a + (toGal(r.capacity) || 0), 0);

  const save = () => {
    if (!name.trim()) return setError(t("Give the trailer a name."));
    if (rows.some((r) => !r.name.trim() || !(Number(r.capacity) > 0))) return setError(t("Every pocket needs a name and a size above 0."));
    if (!(toLb(tare) >= 5000) || !(toLb(maxGross) > toLb(tare))) return setError(t("Empty weight must be at least 5,000 lb and less than the max gross."));
    if (dieselDouble && !rows.some((r) => r.double_bulkhead)) return setError(t("Mark at least one double-bulkhead pocket, or turn off the diesel rule."));
    onSave({ name: name.trim(), diesel_in_double: dieselDouble, tare_weight: keep(tare, initial.tare_weight, toLb), max_gross: keep(maxGross, initial.max_gross, toLb),
      compartments: rows.map((r, i) => ({ ...r, name: r.name.trim(), capacity: keep(r.capacity, initial.compartments[i]?.capacity, toGal) })) });
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal load-modal" onClick={(e) => e.stopPropagation()} data-testid="trailer-modal">
        <div className="card-name">{isNew ? t("New trailer") : t("Edit trailer pockets")}</div>
        <div className="label">{t("Trailer name")}</div>
        <input className="text-input" value={name} onChange={(e) => { setError(""); setName(e.target.value); }} data-testid="trailer-name-input" />
        <div className="load-two">
          <div><div className="label">{METRIC ? t("Empty weight (kg)") : t("Empty weight (lb)")}</div>
            <input className="text-input" inputMode="numeric" value={tare} onChange={(e) => { setError(""); setTare(digits(e.target.value)); }} data-testid="tare-weight-input" /></div>
          <div><div className="label">{METRIC ? t("Max gross (kg)") : t("Max gross (lb)")}</div>
            <input className="text-input" inputMode="numeric" value={maxGross} onChange={(e) => { setError(""); setMaxGross(digits(e.target.value)); }} data-testid="max-gross-input" /></div>
        </div>
        <TareHelper tare={toLb(tare)} maxGross={toLb(maxGross)} onUse={(n) => { setError(""); setTare(shownLb(n)); }} t={t} />
        <div className="card-sub">{t("Front to rear, full shell size of each pocket. Up to 12. Tap DB for double-bulkhead pockets.")}</div>
        {rows.map((r, i) => (
          <div className="load-builder-row" key={i} data-testid={`builder-row-${i}`}>
            <span className="load-step">{i + 1}</span>
            <input className="text-input" value={r.name} maxLength={20} onChange={(e) => setRow(i, "name", e.target.value)} data-testid={`builder-name-${i}`} />
            <input className="text-input" inputMode="numeric" value={r.capacity} onChange={(e) => setRow(i, "capacity", digits(e.target.value))} data-testid={`builder-cap-${i}`} />
            <button type="button" className={`ghost-btn load-db-btn ${r.double_bulkhead ? "on" : ""}`} aria-pressed={!!r.double_bulkhead}
              onClick={() => setRow(i, "double_bulkhead", !r.double_bulkhead)} data-testid={`builder-db-${i}`}>DB</button>
            <button type="button" className="ghost-btn" aria-label={t("Remove")} disabled={rows.length === 1} onClick={() => setRows(rows.filter((_, j) => j !== i))} data-testid={`builder-remove-${i}`}><Trash2 size={14} /></button>
          </div>
        ))}
        <button type="button" className="ghost-btn" disabled={rows.length >= 12} onClick={addRow} data-testid="add-comp-btn">+ {t("Add pocket")}</button>
        <button type="button" className={`ghost-btn load-toggle ${dieselDouble ? "on" : ""}`} aria-pressed={dieselDouble}
          onClick={() => { setError(""); setDieselDouble(!dieselDouble); }} data-testid="diesel-db-toggle">
          <span className="load-switch" />{t("Diesel must go in double-bulkhead pockets")}
        </button>
        <div className="card-sub">{t("Shell total")}: <b>{fmt(total)}</b> gal</div>
        {error && <div className="form-error" data-testid="trailer-form-error">{error}</div>}
        <div className="modal-actions">
          {!isNew && canDelete && <button type="button" className="ghost-btn danger-ghost load-left" onClick={onDelete} data-testid="delete-trailer-btn">{t("Delete")}</button>}
          <button type="button" className="ghost-btn" onClick={onClose} data-testid="cancel-trailer-btn">{t("Cancel")}</button>
          <button type="button" className="primary-btn" onClick={save} data-testid="save-trailer-btn">{t("Save")}</button>
        </div>
      </div>
    </div>
  );
};
